import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LocationAutocomplete from './LocationAutocomplete';
import { shippingAddressesAPI } from '../api';
import { getCurrentLocation, findLocationIds } from '../utils/geolocation';
import { locationsAPI } from '../api';
import toast from 'react-hot-toast';
import {
  isCompleteDeliveryAddress,
  isCompleteSavedAddress,
  isDuplicateAddress,
  isDefaultAddress,
  stripUsCountrySuffix,
} from '../utils/addressUtils';

export type HomeLocationStep = 'address' | 'type' | 'details';

const ADDRESS_TYPES = [
  { id: 'house', label: 'House', icon: 'ri-home-4-line' },
  { id: 'apartment', label: 'Apartment', icon: 'ri-building-line' },
  { id: 'hotel', label: 'Hotel', icon: 'ri-hotel-bed-line' },
  { id: 'office', label: 'Office', icon: 'ri-building-2-line' },
  { id: 'other', label: 'Other', icon: 'ri-map-pin-line' },
] as const;

interface SavedAddress {
  id: number;
  address?: string;
  formatted?: string;
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  is_default?: boolean;
  instructions?: string;
  phone_number?: string;
  address_type?: string;
  apt_suite?: string;
  entry_code?: string;
  building_name?: string;
}

interface HomeLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect?: (cityId: number, stateId: number, cityName: string, stateName: string, fullAddress?: string) => void;
  /** When true, used in settings – save then close, no location select callback */
  settingsMode?: boolean;
  /** Called after save in settings mode so parent can reload addresses */
  onSaved?: () => void;
  /** In settings mode, open with this address pre-selected for editing */
  openForEditId?: number | null;
  /** When true, used in checkout – select or add address, then callback with address data */
  checkoutMode?: boolean;
  /** In checkout mode, which saved address is currently selected (for radio checked state) */
  checkoutSelectedAddressId?: number | null;
  /** Called when address is selected/saved in checkout mode */
  onCheckoutAddressSelect?: (data: {
    deliveryAddress: string;
    addressData: { line1: string; city: string; state: string; postal_code: string; country: string; formatted: string };
    deliveryInstructions: string;
    phoneNumber: string;
    addressType: string | null;
    aptSuite: string;
    entryCode: string;
    buildingName: string;
    addressId?: number;
  }) => void;
  currentCityId?: number;
  currentStateId?: number;
  /** Called when addresses change (e.g. default updated) so parent can refresh */
  onAddressesUpdated?: () => void;
}

const buildAddressData = (addr: SavedAddress) => {
  const full = (addr.address || addr.formatted || '').trim() || [addr.line1, addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ');
  return {
    deliveryAddress: full,
    addressData: {
      line1: addr.line1 || full.split(',')[0]?.trim() || full,
      city: addr.city || '',
      state: addr.state || '',
      postal_code: addr.postal_code || '',
      country: addr.country || 'US',
      formatted: full,
    },
    deliveryInstructions: addr.instructions || '',
    phoneNumber: addr.phone_number || '',
    addressType: (addr.address_type as string) || null,
    aptSuite: addr.apt_suite || '',
    entryCode: addr.entry_code || '',
    buildingName: addr.building_name || '',
    addressId: addr.id,
  };
};

const HomeLocationModal: React.FC<HomeLocationModalProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  settingsMode = false,
  onSaved,
  openForEditId,
  checkoutMode = false,
  checkoutSelectedAddressId,
  onCheckoutAddressSelect,
  onAddressesUpdated,
}) => {
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState<HomeLocationStep>('address');
  const [addressQuery, setAddressQuery] = useState('');
  const [selectedFullAddress, setSelectedFullAddress] = useState<string | null>(null);
  const [addressType, setAddressType] = useState<string>('house');
  const [aptSuite, setAptSuite] = useState('');
  const [entryCode, setEntryCode] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [gateCode, setGateCode] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [hotelName, setHotelName] = useState('');
  const [roomSuite, setRoomSuite] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [leaveAtDoor, setLeaveAtDoor] = useState(true);
  const [leaveLocation, setLeaveLocation] = useState<string>('');
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [useLocationLoading, setUseLocationLoading] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [checkoutAddNew, setCheckoutAddNew] = useState(false);

  const loadSavedAddresses = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingAddresses(true);
    try {
      const list = await shippingAddressesAPI.getAll();
      const arr = Array.isArray(list) ? list : [];
      if (arr.length === 1 && !isDefaultAddress(arr[0] || {})) {
        try { await shippingAddressesAPI.update(arr[0].id, { is_default: true }); } catch (_) {}
        const refreshed = await shippingAddressesAPI.getAll();
        setSavedAddresses(Array.isArray(refreshed) ? refreshed : []);
      } else {
        setSavedAddresses(arr);
      }
    } catch (_) {
      setSavedAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isOpen && isAuthenticated) loadSavedAddresses();
  }, [isOpen, isAuthenticated, loadSavedAddresses]);

  useEffect(() => {
    if (!isOpen) {
      setStep('address');
      setSelectedFullAddress(null);
      setAddressQuery('');
      setEditingAddressId(null);
      setCheckoutAddNew(false);
    }
  }, [isOpen]);

  // In settings mode, when openForEditId is set, pre-select that address for editing
  useEffect(() => {
    if (!isOpen || !settingsMode || !openForEditId || savedAddresses.length === 0) return;
    const addr = savedAddresses.find((a) => a.id === openForEditId);
    if (!addr) return;
    const full = (addr.address || addr.formatted || '').trim();
    if (!full) {
      const parts = [addr.line1, addr.city, addr.state, addr.postal_code].filter(Boolean);
      if (parts.length) setSelectedFullAddress(parts.join(', ') + ', USA');
    } else {
      setSelectedFullAddress(full);
    }
    setEditingAddressId(addr.id);
    setSetAsDefault(isDefaultAddress(addr));
    setAddressQuery(full || (addr.line1 || '') + ', ' + [addr.city, addr.state].filter(Boolean).join(', '));
    setAddressType((addr.address_type as string) || 'house');
    setAptSuite(addr.apt_suite || '');
    setEntryCode(addr.entry_code || '');
    setBuildingName(addr.building_name || '');
    setDeliveryInstructions(addr.instructions || '');
    setStep('details');
  }, [isOpen, settingsMode, openForEditId, savedAddresses]);

  const handlePlaceSelect = useCallback((place: { address: string; city: string; state: string; stateAbbreviation: string; postalCode?: string }) => {
    // place.address (formatted_address) already contains full address – don't append city/state/zip again
    const full = stripUsCountrySuffix(
      (place.address || '').trim() ||
        [place.city, place.state, place.postalCode].filter(Boolean).join(', '),
    );
    if (full) {
      setSelectedFullAddress(full);
      setAddressQuery(full);
      setStep('type');
    }
  }, []);

  const handleUseMyLocation = async () => {
    setUseLocationLoading(true);
    try {
      const location = await getCurrentLocation();
      if (!location.stateAbbreviation || !location.city) {
        toast.error('Could not determine your city. Try entering an address or zip.');
        return;
      }
      const { stateId, cityId } = await findLocationIds(location.city, location.stateAbbreviation);
      if (!stateId || !cityId) {
        toast.error('Your area may not be in our delivery network yet.');
        return;
      }
      const full = [location.city, location.stateAbbreviation].filter(Boolean).join(', ');
      setSelectedFullAddress(full);
      setAddressQuery(full);
      setStep('type');
    } catch (_) {
      toast.error('Location unavailable. Please enter an address or zip.');
    } finally {
      setUseLocationLoading(false);
    }
  };

  const handleSelectSavedAddress = (addr: SavedAddress) => {
    if (checkoutMode) {
      if (!isCompleteSavedAddress(addr)) {
        toast.error('This saved address is incomplete. Add a street address with ZIP or choose another.');
        return;
      }
      onCheckoutAddressSelect?.(buildAddressData(addr));
      onClose();
      return;
    }
    const full = (addr.address || addr.formatted || '').trim();
    if (!full) {
      const parts = [addr.line1, addr.city, addr.state, addr.postal_code].filter(Boolean);
      if (parts.length) setSelectedFullAddress(parts.join(', ') + ', USA');
      else return;
    } else {
      setSelectedFullAddress(full);
    }
    setEditingAddressId(addr.id);
    setSetAsDefault(isDefaultAddress(addr));
    setAddressType((addr.address_type as string) || 'house');
    setAptSuite(addr.apt_suite || '');
    setEntryCode(addr.entry_code || '');
    setBuildingName(addr.building_name || '');
    setDeliveryInstructions(addr.instructions || '');
    setStep('details');
  };

  const handleNextToDetails = () => {
    if (addressType) setStep('details');
  };

  const resolveLocationIds = async (): Promise<{ cityId: number; stateId: number; cityName: string; stateName: string } | null> => {
    const cleaned = stripUsCountrySuffix(selectedFullAddress || '');
    const zipMatch = cleaned.match(/\b(\d{5})(?:-(\d{4}))?\b/);
    if (zipMatch) {
      try {
        const data = await locationsAPI.lookupZipCode(zipMatch[1]);
        if (data?.state_id) {
          let cityId = data.city_id ? Number(data.city_id) : 0;
          const cityName = data.city_name || data.city || '';
          if (!cityId && cityName) {
            try {
              const created = await locationsAPI.createCity(cityName, data.state_id);
              cityId = created?.id ? Number(created.id) : 0;
            } catch (_) {
              const ids = await findLocationIds(
                cityName,
                data.state_abbreviation || data.state || '',
              );
              cityId = ids.cityId || 0;
            }
          }
          if (cityId) {
            const stateRes = await locationsAPI.getStates();
            const state = (stateRes || []).find((s: any) => s.id === data.state_id);
            return {
              cityId,
              stateId: data.state_id,
              cityName: cityName || '',
              stateName: state?.abbreviation || data.state_abbreviation || '',
            };
          }
        }
      } catch (_) {}
    }
    const parts = cleaned.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      // Prefer "City" + "ST ZIP" / "State ZIP" from the end (after country strip).
      let city = '';
      let stateAbbr = '';
      const last = parts[parts.length - 1];
      const stateZip = last.match(/^([A-Za-z]{2}|[A-Za-z][A-Za-z\s]+?)\s+(\d{5})(?:-\d{4})?$/);
      if (stateZip && parts.length >= 2) {
        city = parts[parts.length - 2];
        stateAbbr = stateZip[1].trim();
      } else {
        city = parts.length >= 3 ? parts[parts.length - 2] : parts[0];
        stateAbbr = last.replace(/\s*\d{5}(?:-\d{4})?.*$/, '').trim();
      }
      if (city && stateAbbr) {
        try {
          const { stateId, cityId } = await findLocationIds(city, stateAbbr);
          if (stateId && cityId) {
            const allCities = await locationsAPI.getAllCities();
            const cityObj = (allCities || []).find((c: any) => c.id === cityId);
            const states = await locationsAPI.getStates();
            const stateObj = (states || []).find((s: any) => s.id === stateId);
            return {
              cityId,
              stateId,
              cityName: cityObj?.name || city,
              stateName: stateObj?.abbreviation || stateAbbr,
            };
          }
          if (stateId && city) {
            const created = await locationsAPI.createCity(city, stateId);
            if (created?.id) {
              const states = await locationsAPI.getStates();
              const stateObj = (states || []).find((s: any) => s.id === stateId);
              return {
                cityId: Number(created.id),
                stateId,
                cityName: city,
                stateName: stateObj?.abbreviation || stateAbbr,
              };
            }
          }
        } catch (_) {}
      }
    }
    return null;
  };

  const handleSaveAddress = async () => {
    const full = stripUsCountrySuffix(selectedFullAddress || '');
    if (!full) {
      toast.error('Please enter an address.');
      return;
    }
    const loc = await resolveLocationIds();
    if (!loc) {
      toast.error('We couldn\'t find that area. Try a full address with city and state.');
      return;
    }

    const addressDataForCheck = {
      line1: full.split(',')[0]?.trim() || full,
      city: loc.cityName,
      state: loc.stateName,
      postal_code: (full.match(/\b(\d{5})(?:-(\d{4}))?\b/)?.[1] || '') as string,
      formatted: full,
    };

    if (!isCompleteDeliveryAddress(addressDataForCheck, full)) {
      toast.error('Enter a complete street address with city, state, and ZIP.');
      return;
    }

    // Logged-in: always persist to account (home / settings / checkout) — same as iOS.
    if (isAuthenticated) {
      setSavingAddress(true);
      try {
        const payload = {
          address: full,
          line1: addressDataForCheck.line1,
          city: loc.cityName,
          state: loc.stateName,
          postal_code: addressDataForCheck.postal_code,
          country: 'US',
          instructions: deliveryInstructions,
          address_type: addressType,
          apt_suite: aptSuite || undefined,
          entry_code: entryCode || undefined,
          building_name: buildingName || undefined,
          is_default: setAsDefault,
        };
        if (editingAddressId) {
          await shippingAddressesAPI.update(editingAddressId, payload);
          toast.success('Address updated');
          await loadSavedAddresses();
          onAddressesUpdated?.();
        } else {
          const line1 = payload.line1 || full.split(',')[0] || full;
          const postal_code = payload.postal_code || '';
          if (isDuplicateAddress(savedAddresses, line1, payload.city, payload.state, postal_code, null)) {
            // Already on account — still apply as current delivery location below.
            if (settingsMode || checkoutMode) {
              toast.error('This address is already saved');
              return;
            }
          } else {
            await shippingAddressesAPI.create(payload);
            toast.success('Address saved');
            await loadSavedAddresses();
            onAddressesUpdated?.();
          }
        }
      } catch (e: any) {
        const msg = e?.response?.data?.error || e?.message || 'Could not save address';
        toast.error(msg);
        return;
      } finally {
        setSavingAddress(false);
      }
    }

    if (settingsMode) {
      onSaved?.();
      onClose();
    } else if (checkoutMode) {
      const payload = {
        address: full,
        line1: addressDataForCheck.line1,
        city: loc.cityName,
        state: loc.stateName,
        postal_code: addressDataForCheck.postal_code,
        country: 'US',
        instructions: deliveryInstructions,
        address_type: addressType,
        apt_suite: aptSuite || undefined,
        entry_code: entryCode || undefined,
        building_name: buildingName || undefined,
      };
      onCheckoutAddressSelect?.({
        deliveryAddress: full,
        addressData: {
          line1: payload.line1,
          city: payload.city,
          state: payload.state,
          postal_code: payload.postal_code,
          country: payload.country,
          formatted: full,
        },
        deliveryInstructions,
        phoneNumber: '',
        addressType,
        aptSuite,
        entryCode,
        buildingName,
        addressId: editingAddressId || undefined,
      });
      onClose();
    } else {
      onLocationSelect?.(loc.cityId, loc.stateId, loc.cityName, loc.stateName, full);
      onClose();
    }
  };

  if (!isOpen) return null;

  const addNewPopup = checkoutMode && checkoutAddNew;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Change address modal – full-screen bottom sheet on mobile (same as Home), centered on desktop */}
      {!addNewPopup && (
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md h-[90vh] sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden animate-slide-up sm:animate-none">
        <div className="flex items-center justify-between p-3 border-b border-gray-100">
          {(step !== 'address' || (checkoutMode && checkoutAddNew)) ? (
            <button
              type="button"
              onClick={() => {
                if (checkoutMode && checkoutAddNew) setCheckoutAddNew(false);
                else setStep(step === 'details' ? 'type' : 'address');
              }}
              className="flex items-center gap-1 text-green-600 font-medium text-sm"
            >
              <i className="ri-arrow-left-s-line text-lg" />
              Back
            </button>
          ) : (
            <span />
          )}
          <h2 className="text-lg font-bold text-gray-900">
            {step === 'address' && (checkoutMode ? 'Change delivery address' : settingsMode ? 'Add or select address' : 'Where should we deliver?')}
            {step === 'type' && 'Address type'}
            {step === 'details' && 'Address details'}
          </h2>
          <div className="flex items-center gap-1">
            {step === 'details' && selectedFullAddress && (
              <button
                type="button"
                aria-label="Delete address"
                onClick={async () => {
                  if (editingAddressId) {
                    if (!window.confirm('Delete this address?')) return;
                    try {
                      await shippingAddressesAPI.delete(editingAddressId);
                      toast.success('Address deleted');
                      await loadSavedAddresses();
                      setEditingAddressId(null);
                      setSelectedFullAddress(null);
                      setStep('address');
                      onClose();
                    } catch (_) {
                      toast.error('Could not delete address');
                    }
                  } else {
                    setSelectedFullAddress(null);
                    setStep('address');
                  }
                }}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-600"
              >
                <i className="ri-delete-bin-line text-xl" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
              aria-label="Close"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>
        </div>

        <div className="px-5 pb-6 flex-1 overflow-y-auto min-w-0">
          {/* Step: Address */}
          {step === 'address' && (
            <>
              {checkoutMode && isAuthenticated ? (
                /* Checkout: radio list (mobile-style) */
                <div className="space-y-2" role="radiogroup" aria-label="Delivery address">
                  {loadingAddresses ? (
                    <p className="text-sm text-gray-500">Loading addresses...</p>
                  ) : (
                    <>
                      {savedAddresses.map((addr) => {
                        const full = (addr.address || addr.formatted || '').trim() || [addr.line1, addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ');
                        const isChecked = checkoutSelectedAddressId === addr.id;
                        return (
                          <label
                            key={addr.id}
                            className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              isChecked ? 'border-green-500 bg-green-50/50' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="delivery-address"
                              value={addr.id}
                              checked={isChecked}
                              onChange={() => handleSelectSavedAddress(addr)}
                              className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{full || 'Address'}</p>
                              {isDefaultAddress(addr) && (
                                <span className="inline-block mt-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">Default</span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          setCheckoutAddNew(true);
                          setSelectedFullAddress(null);
                          setEditingAddressId(null);
                        }}
                        className="flex items-center gap-2 w-full text-left text-green-600 hover:text-green-700 font-medium py-3"
                      >
                        <i className="ri-add-line text-xl"></i>
                        Add new address
                      </button>
                    </>
                  )}
                </div>
              ) : (
                /* Home / Settings: normal address step */
                <>
                  <div className="rounded-xl border-2 border-green-500 bg-gray-50 p-4 mb-4">
                    <p className="text-sm font-medium text-green-600 mb-1">Enter your delivery address</p>
                    <LocationAutocomplete
                      inputId="home-location-address-query"
                      value={addressQuery}
                      onInputChange={setAddressQuery}
                      onPlaceSelect={handlePlaceSelect}
                      placeholder="Address, city, or zip code"
                      className="w-full bg-transparent text-gray-900 placeholder-gray-400 text-base focus:outline-none border-0 p-0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Select a suggestion to continue</p>
                  </div>

                  {(selectedFullAddress || (addressQuery.includes(',') && addressQuery.trim().length >= 15)) && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedFullAddress && addressQuery.trim().length >= 15) setSelectedFullAddress(addressQuery.trim());
                        setStep('type');
                      }}
                      className="w-full py-3 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 mb-4"
                    >
                      Next
                    </button>
                  )}

                  {!checkoutMode && !settingsMode && (
                    <button
                      type="button"
                      onClick={handleUseMyLocation}
                      disabled={useLocationLoading}
                      className="flex items-center gap-2 w-full text-left text-gray-900 font-medium py-2.5 mb-4 hover:opacity-80 disabled:opacity-50"
                    >
                      <i className="ri-map-pin-line text-green-600 text-xl" />
                      {useLocationLoading ? 'Detecting...' : 'Use my current location'}
                    </button>
                  )}

                  {isAuthenticated && (
                    <>
                      <h3 className="text-sm font-bold text-gray-900 mb-2">Saved addresses</h3>
                      <div className="space-y-2 mb-4 overflow-hidden">
                        {loadingAddresses ? (
                          <p className="text-sm text-gray-500">Loading...</p>
                        ) : (
                          savedAddresses.map((addr) => {
                            const full = (addr.address || addr.formatted || '').trim() || [addr.line1, addr.city, addr.state].filter(Boolean).join(', ');
                            const isDef = isDefaultAddress(addr);
                            return (
                              <button
                                key={addr.id}
                                type="button"
                                onClick={() => handleSelectSavedAddress(addr)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-left"
                              >
                                <i className="ri-home-4-line text-green-600 text-xl flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{full || 'Address'}</p>
                                </div>
                                {isDef && (
                                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium flex-shrink-0">
                                    <i className="ri-check-line" /> Default
                                  </span>
                                )}
                                <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0" />
                              </button>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}

                  <p className="text-sm text-gray-500">
                    We&apos;ll show items that can be delivered to this address.
                  </p>
                </>
              )}
            </>
          )}

          {/* Step: Type */}
          {step === 'type' && selectedFullAddress && (
            <>
              <p className="text-sm text-gray-600 mb-4">{selectedFullAddress}</p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {ADDRESS_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setAddressType(t.id)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      addressType === t.id ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${t.icon} text-2xl`} />
                    <span className="font-medium text-sm">{t.label}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleNextToDetails}
                className="w-full py-3.5 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700"
              >
                Next
              </button>
            </>
          )}

          {/* Step: Details */}
          {step === 'details' && selectedFullAddress && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street address, city, state, zip</label>
                <input
                  type="text"
                  value={selectedFullAddress}
                  onChange={(e) => setSelectedFullAddress(e.target.value || null)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm"
                  placeholder="Full address"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address type</label>
                <select
                  value={addressType}
                  onChange={(e) => setAddressType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm"
                >
                  {ADDRESS_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              {(addressType === 'apartment' || addressType === 'other') && (
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Apartment/Suite</label>
                    <input type="text" value={aptSuite} onChange={(e) => setAptSuite(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g. 4B" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Entry code</label>
                    <input type="text" value={entryCode} onChange={(e) => setEntryCode(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Gate or building code" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Building name</label>
                    <input type="text" value={buildingName} onChange={(e) => setBuildingName(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Building or complex" />
                  </div>
                </div>
              )}
              {addressType === 'house' && (
                <div className="mb-4">
                  <label className="block text-xs text-gray-500 mb-0.5">Gate code (if applicable)</label>
                  <input type="text" value={gateCode} onChange={(e) => setGateCode(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Optional" />
                </div>
              )}
              {addressType === 'office' && (
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Suite/Floor</label>
                    <input type="text" value={aptSuite} onChange={(e) => setAptSuite(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g. Suite 100" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Business name</label>
                    <input type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Company name" />
                  </div>
                </div>
              )}
              {addressType === 'hotel' && (
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Room/Suite</label>
                    <input type="text" value={roomSuite} onChange={(e) => setRoomSuite(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g. Room 205" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Hotel name</label>
                    <input type="text" value={hotelName} onChange={(e) => setHotelName(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Hotel name" />
                  </div>
                </div>
              )}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Delivery preference</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setLeaveAtDoor(true)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-medium ${leaveAtDoor ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 text-gray-600'}`}
                  >
                    <i className="ri-dropbox-line" /> Leave at door
                  </button>
                  <button
                    type="button"
                    onClick={() => setLeaveAtDoor(false)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-medium ${!leaveAtDoor ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 text-gray-600'}`}
                  >
                    <i className="ri-hand-heart-line" /> Meet at door
                  </button>
                </div>
              </div>
              {isAuthenticated && savedAddresses.length > 0 && (
                <label className="flex items-center gap-2 mb-4">
                  <input type="checkbox" checked={setAsDefault} onChange={(e) => setSetAsDefault(e.target.checked)} className="rounded border-gray-300 text-green-600" />
                  <span className="text-sm text-gray-700">Set as default address</span>
                </label>
              )}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery instructions (optional)</label>
                <textarea
                  value={deliveryInstructions}
                  onChange={(e) => setDeliveryInstructions(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="e.g. ring the bell, leave next to porch"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSaveAddress}
                  disabled={savingAddress}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {savingAddress && <i className="ri-loader-4-line animate-spin text-lg" aria-hidden />}
                  Save address
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {/* Add new address popup – replaces Change modal when open */}
      {addNewPopup && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-gray-100">
              <button
                type="button"
                onClick={() => {
                  if (step === 'address') setCheckoutAddNew(false);
                  else if (step === 'type') setStep('address');
                  else setStep('type');
                }}
                className="flex items-center gap-1 text-green-600 font-medium text-sm"
              >
                <i className="ri-arrow-left-s-line text-lg" />
                Back
              </button>
              <h2 className="text-lg font-bold text-gray-900">
                {step === 'address' && 'Add new address'}
                {step === 'type' && 'Address type'}
                {step === 'details' && 'Address details'}
              </h2>
              {/* Hide close on mobile when on first step (Back does the same); show on desktop or when deeper in flow */}
              {(step !== 'address') ? (
                <button
                  type="button"
                  onClick={() => { setCheckoutAddNew(false); setStep('address'); }}
                  className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                  aria-label="Close"
                >
                  <i className="ri-close-line text-2xl" />
                </button>
              ) : (
                <span className="w-9" />
              )}
            </div>
            <div className="px-5 pb-6 flex-1 overflow-y-auto min-w-0">
              {step === 'address' && (
                <>
                  <div className="rounded-xl border-2 border-green-500 bg-gray-50 p-4 mb-2 mt-4">
                    <p className="text-sm font-medium text-green-600 mb-1">Delivery address</p>
                    <LocationAutocomplete
                      inputId="home-location-checkout-address-query"
                      value={addressQuery}
                      onInputChange={setAddressQuery}
                      onPlaceSelect={handlePlaceSelect}
                      placeholder="Address, city, or zip code"
                      className="w-full bg-transparent text-gray-900 placeholder-gray-400 text-base focus:outline-none border-0 p-0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Select a suggestion to continue</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">Address will auto-fill when you select a suggestion</p>
                  {(selectedFullAddress || (addressQuery.includes(',') && addressQuery.trim().length >= 15)) && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedFullAddress && addressQuery.trim().length >= 15) setSelectedFullAddress(addressQuery.trim());
                        setStep('type');
                      }}
                      className="w-full py-3 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 mb-4"
                    >
                      Next
                    </button>
                  )}
                </>
              )}
              {step === 'type' && selectedFullAddress && (
                <>
                  <p className="text-sm text-gray-600 mb-4 mt-4 truncate">{selectedFullAddress}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {ADDRESS_TYPES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setAddressType(t.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          addressType === t.id ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <i className={`${t.icon} text-2xl`} />
                        <span className="font-medium text-sm">{t.label}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleNextToDetails}
                    className="w-full py-3.5 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700"
                  >
                    Next
                  </button>
                </>
              )}
              {step === 'details' && selectedFullAddress && (
                <>
                  <div className="mb-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street address, city, state, zip</label>
                    <input
                      type="text"
                      value={selectedFullAddress}
                      onChange={(e) => setSelectedFullAddress(e.target.value || null)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm"
                      placeholder="Full address"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address type</label>
                    <select
                      value={addressType}
                      onChange={(e) => setAddressType(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm"
                    >
                      {ADDRESS_TYPES.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  {(addressType === 'apartment' || addressType === 'other') && (
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Apartment/Suite</label>
                        <input type="text" value={aptSuite} onChange={(e) => setAptSuite(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g. 4B" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Entry code</label>
                        <input type="text" value={entryCode} onChange={(e) => setEntryCode(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Gate or building code" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Building name</label>
                        <input type="text" value={buildingName} onChange={(e) => setBuildingName(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Building or complex" />
                      </div>
                    </div>
                  )}
                  {addressType === 'house' && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-500 mb-0.5">Gate code (if applicable)</label>
                      <input type="text" value={gateCode} onChange={(e) => setGateCode(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Optional" />
                    </div>
                  )}
                  {addressType === 'office' && (
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Suite/Floor</label>
                        <input type="text" value={aptSuite} onChange={(e) => setAptSuite(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g. Suite 100" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Business name</label>
                        <input type="text" value={businessName} onChange={(e) => setBuildingName(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Company name" />
                      </div>
                    </div>
                  )}
                  {addressType === 'hotel' && (
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Room/Suite</label>
                        <input type="text" value={roomSuite} onChange={(e) => setRoomSuite(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g. Room 205" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Hotel name</label>
                        <input type="text" value={hotelName} onChange={(e) => setHotelName(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Hotel name" />
                      </div>
                    </div>
                  )}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery instructions (optional)</label>
                    <textarea
                      value={deliveryInstructions}
                      onChange={(e) => setDeliveryInstructions(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="e.g. ring the bell, leave next to porch"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveAddress}
                    disabled={savingAddress}
                    className="w-full py-3.5 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {savingAddress && <i className="ri-loader-4-line animate-spin text-lg" aria-hidden />}
                    Save address
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeLocationModal;
